/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useState } from 'react';
import CountUp from 'react-countup';
// @mui
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { Box, Card, Stack, Typography } from '@mui/material';
// utils
import agent from 'src/utils/agent';
// locales
import { useLocales } from 'src/locales';
//
import AuthStatisticsSkeleton from 'src/components/skeleton/auth-statistics/auth-statistics-skeleton';

// ----------------------------------------------------------------------

const COUNTUP_DURATION = 86400;

// ----------------------------------------------------------------------

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: .35;
  }
  50% {
    transform: scale(1.2);
    opacity: .15;
  }
  100% {
    transform: scale(1);
    opacity: .35;
  }
`;

const glow = keyframes`
  0% {
    opacity: .35;
  }
  50% {
    opacity: .8;
  }
  100% {
    opacity: .35;
  }
`;

// ----------------------------------------------------------------------

type Stat = {
  label: string;
  value: number;
  type: 'images' | 'messages' | 'sessions' | 'tokens';
};

type StatisticsResponse = {
  total_generated_images: number;
  total_messages: number;
  total_sessions: number;
  total_tokens: number;
};

// ----------------------------------------------------------------------

export default function AuthStatisticsLiveStats() {
  const { t } = useLocales();
  const theme = useTheme();

  const [loading, setLoading] = useState<boolean>(true);
  const [statisticsData, setStatisticsData] = useState<StatisticsResponse | null>(null);
  const liveStats: Stat[] = [
    {
      label: t('generated_images'),
      value: 1675,
      type: 'images',
    },
    {
      label: t('answered_messages'),
      value: 279064,
      type: 'messages',
    },
    {
      label: t('sessions_made'),
      value: 23652,
      type: 'sessions',
    },
    // {
    //   label: t('token_spent'),
    //   value: 19873064,
    //   type: 'tokens',
    // },
  ];

  const getStatistics = useCallback(() => {
    agent.Statistics.get()
      .then((response: StatisticsResponse) => {
        // console.log(response);
        setStatisticsData(response ?? null);
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    getStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 3,
        backdropFilter: 'blur(20px)',
        background: alpha(theme.palette.common.white, 0.05),
        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(
                120deg,
                transparent,
                ${alpha(theme.palette.primary.main, 0.08)},
                transparent
              )`,
          animation: `${glow} 4s ease-in-out infinite`,
        }}
      />

      {!loading ? (
        <Stack spacing={3}>
          {liveStats.map((item: Stat, index: number) => (
            <Stack
              key={`live-stat-${index}`}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.success.main,
                    animation: `${pulse} 2s infinite`,
                    boxShadow: `0 0 12px ${theme.palette.success.main}`,
                  }}
                />

                <Typography
                  variant="body1"
                  sx={{
                    // color: alpha(theme.palette.common.white, 0.72),
                    color: 'text.primary',
                  }}
                >
                  {item.label}
                </Typography>
              </Stack>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 1,
                  ml: 'auto',
                  // animation: `${float} 4s ease-in-out infinite`,
                  // animationDelay: item.delay,
                }}
              >
                {/* IMAGE */}
                {item.type === 'images' && (
                  <CountUp
                    start={statisticsData?.total_generated_images ?? item.value}
                    end={item.value * 20}
                    duration={COUNTUP_DURATION}
                    separator=","
                  />
                )}

                {/* MESSAGES */}
                {item.type === 'messages' && (
                  <CountUp
                    start={statisticsData?.total_messages ?? item.value}
                    end={item.value * 1.25}
                    duration={COUNTUP_DURATION}
                    separator=","
                  />
                )}

                {/* SESSIONS */}
                {item.type === 'sessions' && (
                  <CountUp
                    start={statisticsData?.total_sessions ?? item.value}
                    end={item.value * 2.5}
                    duration={COUNTUP_DURATION}
                    separator=","
                  />
                )}

                {/* TOKENS */}
                {item.type === 'tokens' && (
                  <CountUp
                    start={statisticsData?.total_tokens ?? item.value}
                    end={item.value * 2}
                    duration={COUNTUP_DURATION}
                    separator=","
                  />
                )}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : (
        <AuthStatisticsSkeleton />
      )}
    </Card>
  );
}
